import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Clock, Trophy, Users, Ticket } from 'lucide-react';

export default function HistoryPage() {
  const [loading, setLoading] = useState(true);
  const [raffles, setRaffles] = useState([]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await api.getRaffleHistory();
        setRaffles(data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      <h1 className="heading-2">Historial de Sorteos</h1>

      {raffles.length === 0 ? (
        <Card className="card-base text-center py-12" data-testid="no-history">
          <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">Sin historial</h2>
          <p className="text-muted-foreground">
            Aún no hay sorteos completados
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {raffles.map((raffle) => (
            <Card key={raffle.id} className="card-base overflow-hidden" data-testid={`raffle-${raffle.id}`}>
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3">
                <div className="flex items-center justify-between text-white">
                  <span className="font-medium">{raffle.name}</span>
                  <span className="text-sm opacity-80">{formatDate(raffle.created_at)}</span>
                </div>
              </div>
              <CardContent className="p-4 space-y-4">
                {/* Winner */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-900" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Número Ganador</p>
                    <p className="font-mono font-bold text-lg">{raffle.winner_ticket || 'N/A'}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <Trophy className="w-5 h-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground">Premio</p>
                    <p className="font-bold text-sm">{formatCurrency(raffle.prize_amount)}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <Ticket className="w-5 h-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground">Tickets</p>
                    <p className="font-bold text-sm">{raffle.sold_tickets}</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-xl">
                    <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold text-sm">{raffle.total_tickets}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
